import { useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Marker, Popup, useMap } from 'react-map-gl';
import { IconButton } from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { appStore, mapStyles } from 'components/map';
import {
  coordinatePinsAtom,
  movePinAtom,
  openPinIdAtom,
  removePinAtom,
} from './atoms';
import { PinCopyButton } from './copy-feedback';
import { formatDecimalPair, type LngLat, wrapLngLat } from './coordinate-utils';
import styles from './coordinate-pins.module.scss';

/**
 * The dropped pins on one map, and the popup of whichever is open. Drawn on every map
 * through `CoordinatePinsOverlay`, whether or not that map offers the Coordinates tool.
 *
 * @return {JSX.Element} The pins and their popup.
 */
export function PinMarkers(): JSX.Element {
  const { current: mapRef } = useMap();
  const pins = useAtomValue(coordinatePinsAtom, appStore);
  const [openPinId, setOpenPinId] = useAtom(openPinIdAtom, appStore);
  const removePin = useSetAtom(removePinAtom, appStore);
  const movePin = useSetAtom(movePinAtom, appStore);
  const cursorBeforeDrag = useRef('');
  // react-map-gl puts a marker back at its props on every render, so a render mid-drag
  // (a hover popup, say) would snap the pin back to where it started. Its props follow
  // the drag instead.
  const [dragging, setDragging] = useState<{
    id: number;
    lngLat: LngLat;
  } | null>(null);
  // The press and release of a drag land on the pin, so the browser follows a drop
  // with a click; it isn't one.
  const justDragged = useRef(false);
  const openPin = pins.find((pin) => pin.id === openPinId);

  return (
    <>
      {pins.map((pin) => {
        const [longitude, latitude] =
          dragging?.id === pin.id ? dragging.lngLat : pin.lngLat;
        return (
          // Mapbox bakes a marker's colour in when it is created, so a recoloured
          // pin is a new marker.
          <Marker
            key={`${pin.id}-${pin.color}`}
            longitude={longitude}
            latitude={latitude}
            color={pin.color}
            draggable
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              // Otherwise the map sees the click too, and drops a pin or opens a well.
              e.originalEvent.stopPropagation();
              if (justDragged.current) {
                justDragged.current = false;
                return;
              }
              setOpenPinId(pin.id);
            }}
            onDragStart={(e) => {
              setDragging({ id: pin.id, lngLat: wrapLngLat(e.lngLat) });
              // The popup is anchored to where the pin was; it would float there
              // mid-drag.
              if (openPinId === pin.id) setOpenPinId(null);
              // Mapbox turns off the pin's pointer events while it is dragged, so the
              // cursor shown is the canvas's.
              const canvas = mapRef?.getMap().getCanvas();
              if (!canvas) return;
              cursorBeforeDrag.current = canvas.style.cursor;
              canvas.style.cursor = 'grabbing';
            }}
            onDrag={(e) =>
              setDragging({ id: pin.id, lngLat: wrapLngLat(e.lngLat) })
            }
            onDragEnd={(e) => {
              const canvas = mapRef?.getMap().getCanvas();
              if (canvas) canvas.style.cursor = cursorBeforeDrag.current;
              justDragged.current = true;
              // A click doesn't always follow (released off the pin), so the flag
              // can't outlive this turn of the event loop.
              window.setTimeout(() => {
                justDragged.current = false;
              });
              movePin({ id: pin.id, lngLat: wrapLngLat(e.lngLat) });
              setDragging(null);
            }}
          />
        );
      })}
      {openPin && (
        <Popup
          longitude={openPin.lngLat[0]}
          latitude={openPin.lngLat[1]}
          anchor="bottom"
          offset={38}
          closeButton
          // Mapbox focuses the first button on open, and Carbon shows a focused
          // button's tooltip, so "Copy coordinates" would pop up unasked.
          focusAfterOpen={false}
          maxWidth="20rem"
          className={classNames(mapStyles.popup, styles.pinPopup)}
          onClose={() => setOpenPinId(null)}
        >
          <div className={styles.pinPopupBody}>
            <p>
              {openPin.label && (
                <span className={styles.pinPopupLabel}>{openPin.label}</span>
              )}
              {formatDecimalPair(openPin.lngLat)}
            </p>
            <div className={styles.pinPopupActions}>
              <PinCopyButton lngLat={openPin.lngLat} align="top" />
              <IconButton
                kind="ghost"
                size="sm"
                label="Remove pin"
                align="top"
                onClick={() => removePin(openPin.id)}
              >
                <TrashCan />
              </IconButton>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
}
